import { Dialog, Fab, FormControl, InputLabel, MenuItem, Select, Grid, IconButton, Stack, Typography, Box, Button } from '@mui/material'
import { Close } from '@mui/icons-material'
import { useEffect, useState, useRef } from 'react'
import { azureStorageRoutes } from '../routes'
import { useAlertContext } from '../context'
import ImageCropDialog from './ImageCropDialog'

const NuevaMaquinaDialog = ({ open, close, isEdit, nuevaMaquina, editarMaquina, asignarTipoMaquina, marcasMaquinas, tipoMaquinas, modelosMaquinas, maquinaParEditar, maquinasTotales }) => {
    const [marca, setMarca] = useState('')
    const [modelo, setModelo] = useState('')
    const [tipo, setTipo] = useState('')
    const [imagen, setImagen] = useState('')
    const [imagenFile, setImagenFile] = useState('')
    const [modelosFiltrados, setModelosFiltrados] = useState([])
    const { setAlertSimple } = useAlertContext()

    const [rawImageUrl, setRawImageUrl] = useState('')
    const [openCropDialog, setOpenCropDialog] = useState(false)

    useEffect(() => {
        if (maquinaParEditar && Array.isArray(tipoMaquinas) && Array.isArray(modelosMaquinas)) {
            const targetTypeId = typeof maquinaParEditar.typeId === 'object' && maquinaParEditar.typeId !== null
                ? (maquinaParEditar.typeId._id || maquinaParEditar.typeId.id)
                : maquinaParEditar.typeId

            const tipoSeleccionado = tipoMaquinas.find(t => 
                (targetTypeId && String(t._id) === String(targetTypeId)) || 
                (maquinaParEditar.type && t.tipo === maquinaParEditar.type)
            )
            setTipo(tipoSeleccionado?._id || targetTypeId || '')

            if (maquinaParEditar.urlImagen) {
                setImagen(maquinaParEditar.urlImagen)
            } else {
                setImagen('')
            }

            const modeloSeleccionado = modelosMaquinas.find(m => m._id === maquinaParEditar.modelId || m.modelo === (maquinaParEditar.modelo || maquinaParEditar.model))
            if (modeloSeleccionado) {
                setMarca(modeloSeleccionado.marca._id)
                setTimeout(() => {
                    setModelo(modeloSeleccionado._id)
                }, 150)
            }
        } else if (!isEdit) {
            setTipo('')
            setMarca('')
            setModelo('')
            setImagen('')
            setImagenFile('')
        }
    }, [maquinaParEditar, isEdit])

    useEffect(() => {
        if (marca && Array.isArray(modelosMaquinas)) {
            const filtrados = modelosMaquinas.filter(m => m.marca?._id === marca)
            setModelosFiltrados(filtrados)
        } else {
            setModelosFiltrados([])
        }
    }, [marca, modelosMaquinas])

    const ordenPorMarca = (a, b) => {
        if (a.marca > b.marca) return 1
        if (a.marca < b.marca) return -1
        return 0
    }

    const ordenPorTipo = (a, b) => {
        if (a.tipo > b.tipo) return 1
        if (a.tipo < b.tipo) return -1
        return 0
    }

    const ordenPorModelo = (a, b) => {
        if (a.modelo > b.modelo) return 1
        if (a.modelo < b.modelo) return -1
        return 0
    }

    const buscarImagenMaquina = () => {
        const el = document.getElementById('inputImagenMaquina')
        if (el) el.click()
    }

    const imagenSeleccionada = (e) => {
        const file = e.target.files[0]
        if (!file) return

        // Validate size (5MB = 5 * 1024 * 1024)
        if (file.size > 5 * 1024 * 1024) {
            setAlertSimple('Archivo muy grande', 'La imagen no debe superar los 5MB')
            return
        }

        // Validate format
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg']
        if (!validTypes.includes(file.type)) {
            setAlertSimple('Formato incorrecto', 'La imagen debe ser PNG, JPG o JPEG')
            return
        }

        const url = URL.createObjectURL(file)
        setRawImageUrl(url)
        setOpenCropDialog(true)
    }

    const handleCropConfirm = (croppedFile, croppedUrl) => {
        setImagenFile(croppedFile)
        setImagen(croppedUrl)
        setOpenCropDialog(false)
    }

    const guardarMaquina = async () => {
        if (!tipo || !marca || !modelo) {
            setAlertSimple('Campos requeridos', 'Por favor, complete todos los campos obligatorios (Tipo, Marca y Modelo)')
            return
        }

        let res
        if (isEdit && !maquinaParEditar.typeId && tipo) {
            res = await asignarTipoMaquina(maquinaParEditar._id, tipo)
        } else {
            const selectedType = tipoMaquinas.find(t => t._id === tipo)
            const selectedMark = marcasMaquinas.find(m => m._id === marca)
            const selectedModel = modelosMaquinas.find(m => m._id === modelo)

            const machineData = {
                type: selectedType?.tipo || '',
                typeId: tipo,
                brand: selectedMark?.marca || '',
                brandId: marca,
                model: selectedModel?.modelo || '',
                modelId: modelo,
                pIDPM: null,
                urlImagen: maquinaParEditar?.urlImagen || ''
            }

            if (imagenFile) {
                const response = await azureStorageRoutes.uploadMachineImage(imagenFile, 'maquinas/imagenes', 'plataforma-mantencion', `${machineData.model}.png`)
                machineData.urlImagen = response.data.data.url
            }

            if (isEdit) {
                res = await editarMaquina({ ...machineData, _id: maquinaParEditar._id })
            } else {
                res = await nuevaMaquina(machineData)
            }
        }

        if (res) {
            close()
        }
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={close}
                maxWidth={'md'}
            >
                <Box sx={{ width: 600, p: 3 }}>
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                    {isEdit ? 'Editar' : 'Nueva'} Máquina de Proyecto
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Stack spacing={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Tipo:</InputLabel>
                                <Select
                                    value={tipo}
                                    onChange={(e) => setTipo(e.target.value)}
                                    label="Tipo:"
                                >
                                    <MenuItem value={''}>Seleccione Tipo</MenuItem>
                                    {tipoMaquinas.sort(ordenPorTipo).map((t, i) => (
                                        <MenuItem key={i} value={t._id}>{t.tipo}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>Marca:</InputLabel>
                                <Select value={marca} onChange={(e) => setMarca(e.target.value)} label="Marca:">
                                    <MenuItem value={''}>Seleccione Marca</MenuItem>
                                    {marcasMaquinas.sort(ordenPorMarca).map((m, i) => (
                                        <MenuItem key={i} value={m._id}>{m.marca}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>Modelo:</InputLabel>
                                <Select
                                    disabled={(modelosFiltrados || []).length === 0}
                                    value={modelo}
                                    onChange={(e) => setModelo(e.target.value)}
                                    label="Modelo:"
                                >
                                    <MenuItem value={''}>Seleccione Modelo</MenuItem>
                                    {(modelosFiltrados || []).sort(ordenPorModelo).map((m, i) => (
                                        <MenuItem key={i} value={m._id}>{m.modelo}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
                        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                            Imagen de la Máquina
                        </Typography>
                        <Box
                            id={'imagenContainer'}
                            onClick={buscarImagenMaquina}
                            sx={{
                                width: 180,
                                height: 180,
                                border: '2px dashed #ccc',
                                borderRadius: 2,
                                position: 'relative',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                bgcolor: '#fbfbfb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: '#f0f7ff'
                                }
                            }}
                        >
                            {imagen.length > 0 ? (
                                <img src={imagen} alt="Máquina" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            ) : (
                                <Typography variant="caption" align="center" sx={{ color: '#999', px: 2 }}>
                                    Click para subir<br />PNG, JPG, JPEG (Máx 5MB)
                                </Typography>
                            )}
                        </Box>
                        <input id={'inputImagenMaquina'} style={{ display: 'none' }} type="file" accept="image/png, image/jpeg, image/jpg" onChange={imagenSeleccionada} />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" onClick={close} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
                        Cancelar
                    </Button>
                    <Button variant="contained" color="primary" onClick={guardarMaquina} sx={{ borderRadius: 2, textTransform: 'none', px: 4 }}>
                        {isEdit ? 'Actualizar' : 'Guardar'}
                    </Button>
                </Box>
            </Box>

            <IconButton
                onClick={close}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500]
                }}
            >
                <Close />
            </IconButton>
        </Dialog>

        <ImageCropDialog
            open={openCropDialog}
            imageUrl={rawImageUrl}
            onClose={() => setOpenCropDialog(false)}
            onConfirm={handleCropConfirm}
        />
    </>
    )
}

export default NuevaMaquinaDialog
